// GoalsRoom — the gallery (concept C, Paul's pick, Nov 4 2026: "the gallery
// speaks to me. I see these containers as separate, and the hierarchy makes
// sense"). One card per goal: the name, the plan's first lines, the projects
// with their states, plain counts. Behind G and the wall masthead's quiet
// "manage goals" word — NEVER a boot destination: the wall stays the only
// home, this room opens as an overlay on top of it and Esc puts it away.
//
// The room's contract, by construction: plain counts only (no meters, no
// denominators), menus open on click and close on Esc/outside (never hover),
// nothing records visits, and nothing here needs daily tending. Cards drag
// to reorder goals — the wall's chips follow, one order everywhere.
import React, { useEffect, useRef, useState } from 'react';
import ActionEditor from './ActionEditor';
import { byGoalOrder } from '../goalOrder';
import { STATE_WORDS } from '../stateWords';

const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";
// Each goal wears a small identity tick. Muted hues of like lightness so
// they hold in every theme without shouting in any.
export const GOAL_HUES = ['#A65B41', '#4A6E8A', '#6E7D52', '#7A6292', '#8A6F4A', '#4A8A7E'];
// One hue rule for every surface that paints a goal (the gallery, the workbench).
export const goalHue = (g) =>
  GOAL_HUES[(((g?.id ?? 0) % GOAL_HUES.length) + GOAL_HUES.length) % GOAL_HUES.length];

export default function GoalsRoom({
  goals, projects, actions, completedActions,
  updateGoal, updateProject, addGoalInline, addProjectInline, reorderGoals,
  openLibrary, openProjectSolo, onOpenGoal, onClose,
}) {
  const [openPlanIds, setOpenPlanIds] = useState(() => new Set());
  const [menuFor, setMenuFor] = useState(null);      // goal id with ··· open
  const [moveFor, setMoveFor] = useState(null);      // goal id with move-select open
  const [newProjFor, setNewProjFor] = useState(null);// goal id with new-project input open
  const [closingFor, setClosingFor] = useState(null);// goal id with the landing input open
  const [landing, setLanding] = useState('');
  const [addingGoal, setAddingGoal] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const [dragId, setDragId] = useState(null);        // goal card being dragged
  const menuRef = useRef(null);

  // menus: click to open, outside click or Esc closes — never hover (law 5).
  // Esc with no menu open falls through to the app's ladder, which closes
  // the room itself.
  useEffect(() => {
    if (menuFor == null) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuFor(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMenuFor(null); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [menuFor]);

  const active = goals.filter(g => g.active && !g.setAside).sort(byGoalOrder);
  const resting = goals.filter(g => g.active && g.setAside).sort(byGoalOrder);
  const projOf = (gid) => projects.filter(p => p.active && p.goalId === gid);
  const hueOf = (g) => GOAL_HUES[((g.id ?? 0) % GOAL_HUES.length + GOAL_HUES.length) % GOAL_HUES.length];

  // A project reads as doing when any of its open tasks is in motion;
  // otherwise to do. (Done projects left the card with their landing line.)
  const openTasksOf = (pid) => actions.filter(a => a.linkType === 'project' && a.linkId === pid);
  const doneTasksOf = (pid) => completedActions.filter(a => a.linkType === 'project' && a.linkId === pid);
  const projState = (p) => openTasksOf(p.id).some(a => a.status === 'doing' || a.status === 'done')
    ? STATE_WORDS.doing : STATE_WORDS.todo;
  const taskWords = (p) => {
    const open = openTasksOf(p.id).length, done = doneTasksOf(p.id).length;
    if (done > 0) return `${done} of ${open + done} tasks done`;
    return `${open} task${open === 1 ? '' : 's'}`;
  };

  // The plan's first lines, flattened to plain words for the card.
  const planExcerpt = (notes) => {
    if (!notes || !notes.trim()) return null;
    const flat = notes.replace(/[#*_`>]/g, '').split('\n').map(s => s.trim()).filter(Boolean).slice(0, 2).join(' ');
    return flat.length > 150 ? flat.slice(0, 150).trimEnd() + '…' : flat;
  };

  // Dragging a card's tick reorders the goals; dropping on a card lands
  // before it, dropping on the + new goal card lands last. One order law.
  const dropBefore = (targetId) => {
    if (dragId == null || dragId === targetId) return;
    const ids = active.map(g => g.id).filter(id => id !== dragId);
    const at = targetId == null ? ids.length : ids.indexOf(targetId);
    ids.splice(at === -1 ? ids.length : at, 0, dragId);
    if (reorderGoals) reorderGoals(ids);
    setDragId(null);
  };

  // Completing a goal: the same semantics the shelf used — the goal keeps its
  // landing line and closedDate, and its projects stand down with it.
  const completeGoal = (g) => {
    updateGoal(g.id, { active: false, landing: landing.trim() || null, closedDate: new Date().toISOString() });
    projOf(g.id).forEach(p => updateProject(p.id, { active: false }));
    setClosingFor(null); setLanding('');
  };

  const label = { fontFamily: MONO, letterSpacing: '0.13em', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' };
  const sword = { fontFamily: MONO, letterSpacing: '0.1em', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 };

  return (
    <div className="fixed inset-0 themed-background overflow-y-auto text-left" style={{ zIndex: 40 }}>
      <style>{`
        .goal-card { transition: box-shadow .15s ease, transform .15s ease; }
        .goal-card:hover { box-shadow: 0 12px 30px rgba(0,0,0,0.13); transform: translateY(-2px); }
        .goal-card .open-chip { opacity: 0; transition: opacity .15s ease; }
        .goal-card:hover .open-chip, .goal-card:focus-within .open-chip { opacity: 1; }
        .goal-proj:hover { color: var(--color-primary); }
      `}</style>

      {/* masthead — sibling of the wall's */}
      <div className="flex items-baseline gap-4 px-6 border-b themed-border-light" style={{ height: 52, lineHeight: '52px' }}>
        <span className="text-[15px] font-bold themed-text select-none" style={{ fontFamily: MONO, letterSpacing: '0.03em' }}>Phōkii</span>
        <span className="themed-text-muted select-none" style={label}>Goals — the planning room</span>
        <span className="flex-1" />
        <button onClick={onClose} className="text-[12.5px] themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer">esc — back to the wall</button>
      </div>

      <div className="mx-auto px-6 pb-24" style={{ maxWidth: 1240, paddingTop: 30 }}>
        <p className="text-[12.5px] themed-text-muted" style={{ marginBottom: 24 }}>
          One card per goal — its plan and the projects that belong to it. Visited on planning day; the wall never brings you here.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 22 }}>
          {active.map(g => {
            const gp = projOf(g.id);
            const planOpen = openPlanIds.has(g.id);
            const others = projects.filter(p => p.active && p.goalId !== g.id);
            const excerpt = planExcerpt(g.notes);
            const doing = gp.filter(p => projState(p) === STATE_WORDS.doing).length;
            const starred = gp.filter(p => p.pho).length;
            const empty = gp.length === 0;
            return (
              <section key={g.id}
                className={`goal-card rounded-2xl border ${empty ? '' : 'themed-surface'}`}
                onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); dropBefore(g.id); }}
                style={{
                  borderColor: 'var(--color-border)',
                  borderStyle: empty ? 'dashed' : 'solid',
                  padding: '20px 22px 16px',
                  display: 'flex', flexDirection: 'column', minHeight: 230,
                  ...(dragId === g.id ? { opacity: 0.35 } : null),
                }}>
                <div className="flex items-center gap-2.5">
                  <span draggable
                    onDragStart={() => setDragId(g.id)} onDragEnd={() => setDragId(null)}
                    title="Drag to reorder goals — the wall's chips follow"
                    style={{ width: 11, height: 11, borderRadius: 3, background: empty ? 'var(--color-border)' : hueOf(g), flexShrink: 0, cursor: 'grab' }} />
                  <h2 className={`text-[19px] ${empty ? 'themed-text-muted' : 'themed-text'} min-w-0 truncate`}
                    style={{ fontWeight: 650, letterSpacing: '-0.01em', margin: 0 }}
                    contentEditable suppressContentEditableWarning title="Click to rename"
                    onBlur={(e) => { const t = e.target.innerText.trim(); if (t && t !== g.name) updateGoal(g.id, { name: t }); else e.target.innerText = g.name; }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } if (e.key === 'Escape') { e.target.innerText = g.name; e.target.blur(); } }}
                  >{g.name}</h2>
                  {onOpenGoal && !empty && (
                    <button onClick={() => onOpenGoal(g.id)}
                      className="open-chip text-[11px] themed-text-muted hover:themed-text border themed-border rounded-full bg-transparent cursor-pointer flex-shrink-0"
                      style={{ padding: '1px 10px' }} title="Open this goal's page">open ↵</button>
                  )}
                  <span className="relative ml-auto flex-shrink-0" ref={menuFor === g.id ? menuRef : undefined}>
                    <button onClick={() => setMenuFor(m => m === g.id ? null : g.id)} aria-haspopup="menu" aria-expanded={menuFor === g.id}
                      className="themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer px-2 rounded" style={{ fontSize: 15 }}>···</button>
                    {menuFor === g.id && (
                      <div role="menu" className="absolute right-0 themed-background border themed-border rounded-lg shadow-lg py-1" style={{ top: 26, minWidth: 200, zIndex: 20 }}>
                        <button onClick={() => { setMenuFor(null); setNewProjFor(g.id); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text hover:themed-surface-hover bg-transparent border-0 cursor-pointer">New project in this goal…</button>
                        <button onClick={() => { setMenuFor(null); setMoveFor(g.id); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Move a project here…</button>
                        <button onClick={() => { setMenuFor(null); updateGoal(g.id, { setAside: true }); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text-muted hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Set aside — rest it off the wall</button>
                        <button onClick={() => { setMenuFor(null); setClosingFor(g.id); setLanding(''); }} className="block w-full text-left px-3 py-1.5 text-[13px] themed-text-muted hover:themed-surface-hover bg-transparent border-0 cursor-pointer">Complete… — how did it end?</button>
                      </div>
                    )}
                  </span>
                </div>

                {/* the plan: the why, readable without a click */}
                <div style={{ marginTop: 9 }}>
                  {excerpt && !planOpen && (
                    <p className="text-[12.5px] themed-text-muted" style={{ margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{excerpt}</p>
                  )}
                  <button onClick={() => setOpenPlanIds(s => { const n = new Set(s); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })}
                    className="themed-primary bg-transparent border-0 cursor-pointer p-0 text-[12px]" style={{ marginTop: excerpt && !planOpen ? 4 : 0 }}>
                    {planOpen ? 'close the plan' : (excerpt ? 'the whole plan →' : 'write the plan — why does this goal exist?')}
                  </button>
                  {planOpen && (
                    <div style={{ borderLeft: '2px solid var(--color-border)', paddingLeft: 14, marginTop: 8 }} className="foreword-compact">
                      <ActionEditor key={`plan-${g.id}`} content={g.notes || ''} onSave={(md) => updateGoal(g.id, { notes: md })} />
                    </div>
                  )}
                </div>

                {/* the projects — the hierarchy, visible */}
                {empty && newProjFor !== g.id && moveFor !== g.id ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>
                    <p className="text-[12.5px] themed-text-muted" style={{ margin: 0, textAlign: 'center', lineHeight: 1.6 }}>
                      nothing here yet<br />
                      <button onClick={() => setNewProjFor(g.id)} className="themed-primary bg-transparent border-0 cursor-pointer p-0 text-[12.5px]">+ new project</button>
                      {' · '}
                      <button onClick={() => updateGoal(g.id, { setAside: true })} className="themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer p-0 text-[12.5px]">set it aside to rest</button>
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    {gp.map((p, i) => {
                      const st = projState(p);
                      const inMotion = st === STATE_WORDS.doing;
                      return (
                        <button key={p.id} onClick={() => { onClose(); openProjectSolo(p.id); }}
                          className="goal-proj w-full text-left bg-transparent border-0 cursor-pointer flex items-center gap-2.5 themed-text"
                          style={{ padding: '6.5px 0', borderBottom: i < gp.length - 1 ? '1px solid var(--color-borderLight, var(--color-border))' : 'none' }}
                          title={`${p.name} — ${taskWords(p)}. Open this project's room`}>
                          <span style={{
                            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                            ...(inMotion ? { background: 'var(--color-primary)' } : { border: '1.5px solid var(--color-textMuted)' }),
                          }} />
                          <span className="text-[13.5px] min-w-0 truncate" style={{ flex: 1 }}>{p.name}</span>
                          {p.pho && <span style={{ color: 'var(--color-primary)', fontSize: 11, flexShrink: 0 }}>✦</span>}
                          <span style={{ ...sword, color: inMotion ? 'var(--color-primary)' : 'var(--color-textMuted)' }}>{st}</span>
                        </button>
                      );
                    })}
                    {newProjFor === g.id && (
                      <input autoFocus placeholder="New project…" className="w-full text-[13px] themed-text bg-transparent border-0 border-b themed-border focus:outline-none" style={{ padding: '6px 0' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) { addProjectInline(e.target.value.trim(), g.id); setNewProjFor(null); }
                          if (e.key === 'Escape') { e.stopPropagation(); setNewProjFor(null); }
                        }} onBlur={() => setNewProjFor(null)} />
                    )}
                    {moveFor === g.id && (
                      <select autoFocus className="text-[12px] themed-text themed-background border themed-border rounded" style={{ marginTop: 8 }}
                        onChange={(e) => { const id = parseInt(e.target.value, 10); if (Number.isInteger(id)) updateProject(id, { goalId: g.id }); setMoveFor(null); }}
                        onBlur={() => setMoveFor(null)}>
                        <option value="">Move which project here?</option>
                        {others.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    )}
                  </div>
                )}

                {/* completing asks one line — the landing is the point */}
                {closingFor === g.id && (
                  <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                    <input autoFocus value={landing} onChange={(e) => setLanding(e.target.value)}
                      placeholder="How did it end? One line — searchable later."
                      className="flex-1 text-[12.5px] themed-text themed-background border themed-border rounded-lg px-3 py-1.5 focus:outline-none"
                      onKeyDown={(e) => { if (e.key === 'Enter') completeGoal(g); if (e.key === 'Escape') { e.stopPropagation(); setClosingFor(null); } }} />
                    <button onClick={() => completeGoal(g)} className="text-[12px] themed-button-primary rounded-lg px-3 py-1.5 border-0 cursor-pointer flex-shrink-0">Complete it</button>
                  </div>
                )}

                {/* plain counts, no meters */}
                {!empty && (
                  <p className="text-[11.5px] themed-text-muted" style={{ margin: 0, marginTop: 'auto', paddingTop: 14 }}>
                    {gp.length} project{gp.length === 1 ? '' : 's'}
                    {gp.length > 0 && ` — ${doing} doing, ${gp.length - doing} to do`}
                    {starred > 0 && ` · ${starred} starred`}
                  </p>
                )}
              </section>
            );
          })}

          {/* + new goal — the last card, and the drop zone for "make it last" */}
          <section className="rounded-2xl border themed-border" style={{ borderStyle: 'dashed', padding: '20px 22px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 230 }}
            onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); dropBefore(null); }}>
            {addingGoal ? (
              <input autoFocus value={newGoalName} onChange={(e) => setNewGoalName(e.target.value)} placeholder="Name the goal…"
                className="text-[14px] themed-text themed-background border themed-border rounded-lg px-3 py-2 focus:outline-none" style={{ width: '85%' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newGoalName.trim()) { addGoalInline(newGoalName.trim()); setNewGoalName(''); setAddingGoal(false); }
                  if (e.key === 'Escape') { e.stopPropagation(); setAddingGoal(false); setNewGoalName(''); }
                }} onBlur={() => { setAddingGoal(false); setNewGoalName(''); }} />
            ) : (
              <button onClick={() => setAddingGoal(true)} className="text-[13.5px] themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer">+ new goal</button>
            )}
          </section>
        </div>

        <section style={{ marginTop: 40 }}>
          <h3 className="themed-text-muted" style={{ ...label, marginBottom: 10 }}>Resting</h3>
          {resting.length ? resting.map(g => (
            <div key={g.id} className="text-[13px] themed-text" style={{ marginTop: 6 }}>
              {g.name} <span className="themed-text-muted">— set aside</span>{' '}
              <button onClick={() => updateGoal(g.id, { setAside: false })} className="themed-primary bg-transparent border-0 cursor-pointer p-0 text-[12px]">pick it back up</button>
            </div>
          )) : (
            <p className="text-[13px] themed-text-muted italic">Nothing set aside. Park a goal from its ··· and it rests here, off the wall’s lens, until you pick it back up.</p>
          )}
        </section>

        <section className="border-t themed-border-light" style={{ marginTop: 44, paddingTop: 22 }}>
          <h3 className="themed-text-muted" style={{ ...label, marginBottom: 10 }}>Start from somewhere</h3>
          <button onClick={() => { onClose(); openLibrary(); }} className="text-[13.5px] themed-text hover:themed-primary bg-transparent border-0 cursor-pointer p-0">Goals other people made →</button>
          <p className="text-[12px] themed-text-muted" style={{ marginTop: 5, maxWidth: '58ch' }}>
            Four hundred written plans. Read one whole, take it, and it becomes yours to rewrite.
          </p>
        </section>

        <p className="text-[11.5px] themed-text-muted" style={{ marginTop: 46 }}>
          This room opens from <kbd className="text-[10px] border themed-border rounded px-1">G</kbd> or “manage goals” in the wall’s masthead. Drag a card’s color tick to reorder goals — the wall’s chips follow. Nothing counts your visits; nothing here needs daily tending.
        </p>
      </div>
    </div>
  );
}
